// lib/storage.ts
import { useState } from 'react';
import supabase from './supabase';

export interface UploadConfig {
  userId: string;
  contextId: string; // conversation_id, community_id, etc.
  contextType: 'conversation' | 'community' | 'profile';
  file: File;
  maxSizeBytes?: number;
}

export interface StorageFile {
  path: string;
  type: string;
  size: number;
  url: string;
  name: string;
  signedUrl?: string;
}

export class StorageService {
  private static readonly DEFAULT_MAX_SIZE = 10 * 1024 * 1024; // 10MB
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_DELAY = 1000;

  /**
   * Get the appropriate storage bucket based on context type
   */
  private static getBucket(contextType: string): string {
    switch (contextType) {
      case 'conversation':
        return 'message_attachments';
      case 'community':
        return 'community-attachments'; // Note: hyphen, not underscore
      case 'profile':
        return 'avatars';
      default:
        throw new Error(`Unknown context type: ${contextType}`);
    }
  }

  /**
   * Generate consistent file path: user_id/context_id/filename
   */
  private static generateFilePath(userId: string, contextId: string, filename: string): string {
    const fileExt = filename.split('.').pop();
    const uniqueFilename = `${crypto.randomUUID()}.${fileExt}`;
    return `${userId}/${contextId}/${uniqueFilename}`;
  }

  /**
   * Upload file with retry mechanism
   */
  private static async uploadFileWithRetry(
    bucket: string,
    filePath: string,
    file: File
  ): Promise<string> {
    let attempt = 0;
    
    while (attempt < this.MAX_RETRIES) {
      try {
        const { error: uploadError } = await supabase.storage
          .from(bucket)
          .upload(filePath, file, {
            cacheControl: '3600',
            contentType: file.type,
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Always return the file path for consistent handling
        // Signed URLs will be generated when displaying
        return filePath;
      } catch (error: any) {
        attempt++;
        console.warn(`Upload attempt ${attempt} failed:`, error);
        
        if (attempt === this.MAX_RETRIES) {
          throw error;
        }
        
        await new Promise(resolve => 
          setTimeout(resolve, this.RETRY_DELAY * Math.pow(2, attempt))
        );
      }
    }

    throw new Error('Upload failed after maximum retries');
  }

  /**
   * Upload single file with consistent structure
   */
  static async uploadFile(config: UploadConfig): Promise<StorageFile> {
    const { userId, contextId, contextType, file, maxSizeBytes = this.DEFAULT_MAX_SIZE } = config;

    console.log('Starting file upload:', {
      fileName: file.name,
      fileSize: file.size,
      contextType,
      userId,
      contextId
    });

    // Validate file size
    if (file.size > maxSizeBytes) {
      throw new Error(`File ${file.name} is too large. Maximum size is ${maxSizeBytes / (1024 * 1024)}MB.`);
    }

    const bucket = this.getBucket(contextType);
    const filePath = this.generateFilePath(userId, contextId, file.name);

    console.log('Upload details:', { bucket, filePath });

    try {
      const returnedPath = await this.uploadFileWithRetry(bucket, filePath, file);

      const result = {
        path: filePath,
        type: file.type,
        size: file.size,
        url: returnedPath, // This will be the file path, not a URL
        name: file.name
      };

      console.log('Upload successful:', result);
      return result;
    } catch (error: any) {
      console.error('Upload failed:', error);
      throw new Error(error.message || 'Failed to upload file. Please try again.');
    }
  }

  /**
   * Upload multiple files
   */
  static async uploadFiles(
    files: File[],
    config: Omit<UploadConfig, 'file'>
  ): Promise<StorageFile[]> {
    const uploadPromises = files.map(file => 
      this.uploadFile({ ...config, file })
    );

    return Promise.all(uploadPromises);
  }

  /**
   * Get signed URL for private storage access
   */
  static async getSignedUrl(
    bucket: string,
    path: string,
    expiresIn: number = 3600
  ): Promise<string> {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, expiresIn);

      if (error) {
        // If file doesn't exist, return empty string instead of throwing
        if (error.message.includes('Object not found')) {
          console.warn(`File not found: ${path}`);
          return '';
        }
        throw error;
      }
      
      return data.signedUrl;
    } catch (error: any) {
      console.error('Error creating signed URL:', error);
      return ''; // Return empty string instead of throwing
    }
  }

  /**
   * Get signed URLs for multiple files
   */
  static async getSignedUrls(
    bucket: string,
    paths: string[],
    expiresIn: number = 3600
  ): Promise<{ [path: string]: string }> {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrls(paths, expiresIn);

      if (error) {
        console.error('Error creating signed URLs:', error);
        // Return empty object if batch fails
        return {};
      }

      const signedUrls: { [path: string]: string } = {};
      data.forEach((item, index) => {
        if (item.signedUrl && !item.error) {
          signedUrls[paths[index]] = item.signedUrl;
        } else {
          console.warn(`Failed to create signed URL for: ${paths[index]}`, item.error);
          signedUrls[paths[index]] = ''; // Empty string for failed URLs
        }
      });

      return signedUrls;
    } catch (error: any) {
      console.error('Error creating signed URLs:', error);
      return {}; // Return empty object instead of throwing
    }
  }

  /**
   * Add signed URLs to storage files
   */
  static async addSignedUrls(
    files: StorageFile[],
    contextType: string,
    expiresIn: number = 3600
  ): Promise<StorageFile[]> {
    if (!files.length) return files;

    const bucket = this.getBucket(contextType);
    const paths = files.map(f => f.path);

    console.log(`Generating signed URLs for bucket: ${bucket}`, paths);

    try {
      const signedUrls = await this.getSignedUrls(bucket, paths, expiresIn);
      
      const result = files.map(file => {
        const signedUrl = signedUrls[file.path];
        
        // Check if the original URL is a private URL placeholder or public URL
        let finalUrl = signedUrl;
        
        if (!signedUrl) {
          // If signed URL generation failed, check if it's a private bucket
          if (file.url && file.url.startsWith('private://')) {
            // Private bucket but no signed URL - this is an error
            console.error(`Failed to generate signed URL for private file: ${file.path}`);
            finalUrl = ''; // Don't show anything rather than broken link
          } else {
            // Public bucket or existing public URL
            finalUrl = file.url;
          }
        }
        
        console.log(`File: ${file.path}, Original URL: ${file.url}, Signed URL: ${signedUrl}, Final URL: ${finalUrl}`);
        
        return {
          ...file,
          signedUrl: finalUrl
        };
      });

      return result;
    } catch (error) {
      console.error('Error adding signed URLs:', error);
      // Return files with original URLs if signing fails
      return files.map(file => ({
        ...file,
        signedUrl: file.url && !file.url.startsWith('private://') ? file.url : ''
      }));
    }
  }

  /**
   * Remove file from storage
   */
  static async removeFile(bucket: string, path: string): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) throw error;
    } catch (error: any) {
      console.error('Error removing file:', error);
      throw error;
    }
  }

  /**
   * Remove multiple files from storage
   */
  static async removeFiles(bucket: string, paths: string[]): Promise<void> {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove(paths);

      if (error) throw error;
    } catch (error: any) {
      console.error('Error removing files:', error);
      throw error;
    }
  }

  /**
   * Clean up orphaned files (optional utility)
   */
  static async cleanupOrphanedFiles(
    bucket: string,
    userPath: string,
    validPaths: string[]
  ): Promise<void> {
    try {
      const { data: files, error } = await supabase.storage
        .from(bucket)
        .list(userPath, { limit: 1000 });

      if (error) throw error;

      const orphanedPaths = files
        ?.filter(file => !validPaths.includes(`${userPath}/${file.name}`))
        .map(file => `${userPath}/${file.name}`) || [];

      if (orphanedPaths.length > 0) {
        await this.removeFiles(bucket, orphanedPaths);
        console.log(`Cleaned up ${orphanedPaths.length} orphaned files`);
      }
    } catch (error: any) {
      console.error('Error cleaning up orphaned files:', error);
    }
  }
}

// Helper hook for file uploads
export function useFileUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadFiles = async (
    files: File[],
    config: Omit<UploadConfig, 'file'>
  ): Promise<StorageFile[]> => {
    try {
      setIsUploading(true);
      setUploadError(null);
      
      return await StorageService.uploadFiles(files, config);
    } catch (error: any) {
      setUploadError(error.message);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFile = async (config: UploadConfig): Promise<StorageFile> => {
    try {
      setIsUploading(true);
      setUploadError(null);
      
      return await StorageService.uploadFile(config);
    } catch (error: any) {
      setUploadError(error.message);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFiles,
    uploadFile,
    isUploading,
    uploadError,
    clearError: () => setUploadError(null)
  };
}